import { FormEvent, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Linkedin, Menu, X, Youtube } from 'lucide-react';
import { config } from '../lib/config';
import { CookieBanner } from './CookieBanner';

type MenuItem = {
  label: string;
  path: string;
  children?: Array<{ label: string; path: string; description: string }>;
};

const menus: MenuItem[] = [
  {
    label: 'Platform',
    path: '/platform',
    children: [
      { label: 'Platform overview', path: '/platform', description: 'See the connected CoopCore product architecture.' },
      { label: 'Member experience', path: '/platform#member-experience', description: 'Explore member-first mobile and self-service.' },
      { label: 'Security architecture', path: '/security', description: 'Review identity, tenant and workflow controls.' },
    ],
  },
  {
    label: 'Solutions',
    path: '/solutions',
    children: [
      { label: 'Primary cooperatives', path: '/solutions#primary', description: 'Run member and financial operations in one system.' },
      { label: 'Secondary unions', path: '/solutions#unions', description: 'Coordinate affiliated societies and oversight.' },
      { label: 'Supporting organisations', path: '/solutions#partners', description: 'Deliver authorised services with tenant control.' },
    ],
  },
  {
    label: 'Resources',
    path: '/resources',
    children: [
      { label: 'Product guide', path: '/resources#product-guide', description: 'Understand the platform and operating model.' },
      { label: 'Security overview', path: '/security', description: 'Review the implemented security controls.' },
      { label: 'Pricing', path: '/pricing', description: 'Load current public plans from CoopCore.' },
    ],
  },
  {
    label: 'Company',
    path: '/company',
    children: [
      { label: 'About CoopCore', path: '/company', description: 'Learn why CoopCore is built for cooperatives.' },
      { label: 'Contact', path: '/contact', description: 'Request a product walkthrough.' },
    ],
  },
  { label: 'Pricing', path: '/pricing' },
];

export function Layout() {
  const [open, setOpen] = useState(false);
  const [footerEmail, setFooterEmail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    setOpen(false);
    if (location.hash) {
      window.setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' }), 60);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.hash]);

  const continueUpdates = (event: FormEvent) => {
    event.preventDefault();
    const email = footerEmail.trim();
    navigate(email ? `/contact?email=${encodeURIComponent(email)}` : '/contact');
  };

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className={isHome ? 'site-header site-header-home' : 'site-header'}>
        <div className="container nav-wrap">
          <Link className="brand-wordmark" to="/" aria-label="CoopCore home"><img src="/coopcore-icon.png" alt="" /><strong>CoopCore</strong></Link>
          <button className="nav-toggle" aria-expanded={open} aria-label="Toggle navigation" onClick={() => setOpen(value => !value)}>{open ? <X /> : <Menu />}</button>
          <nav className={open ? 'main-nav main-nav-open' : 'main-nav'} aria-label="Primary navigation">
            {menus.map(menu => (
              <div className="nav-menu" key={menu.path}>
                <NavLink to={menu.path} className={({ isActive }) => isActive ? 'nav-link nav-link-active' : 'nav-link'}>
                  {menu.label}{menu.children ? <ChevronDown /> : null}
                </NavLink>
                {menu.children ? (
                  <div className="nav-dropdown">
                    {menu.children.map(child => (
                      <Link key={child.path} to={child.path}><strong>{child.label}</strong><small>{child.description}</small></Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            <a className="nav-link nav-signin" href={`${config.appUrl}/login`}>Sign in</a>
            <Link className="home-button home-button-accent nav-cta" to="/contact">Request a demo <ArrowRight /></Link>
          </nav>
        </div>
      </header>
      <main id="main-content"><Outlet /></main>
      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <Link className="brand-wordmark" to="/"><img src="/coopcore-icon.png" alt="" /><strong>CoopCore</strong></Link>
            <p>The finance and operations platform built for cooperatives that are ready to grow with control.</p>
            <div className="social-links"><a href="https://www.linkedin.com" aria-label="LinkedIn"><Linkedin /></a><a href="https://www.youtube.com" aria-label="YouTube"><Youtube /></a></div>
          </div>
          <div><h3>Platform</h3><Link to="/platform">Overview</Link><Link to="/platform#member-experience">Member experience</Link><Link to="/security">Security</Link><Link to="/resources">Integrations</Link></div>
          <div><h3>Solutions</h3><Link to="/solutions#primary">Primary cooperatives</Link><Link to="/solutions#unions">Secondary unions</Link><Link to="/solutions#partners">Supporting organisations</Link><Link to="/solutions#enterprise">Enterprise operations</Link></div>
          <div><h3>Resources</h3><Link to="/resources#product-guide">Product guide</Link><Link to="/security">Security overview</Link><Link to="/pricing">Pricing</Link><a href={`mailto:${config.supportEmail}`}>Help centre</a></div>
          <div><h3>Company</h3><Link to="/company">About us</Link><Link to="/contact">Contact us</Link><a href={`${config.appUrl}/login`}>Sign in</a></div>
          <div className="footer-updates"><h3>Stay updated</h3><p>Get product and cooperative operations updates.</p><form onSubmit={continueUpdates}><label className="sr-only" htmlFor="footer-email">Work email</label><input id="footer-email" type="email" value={footerEmail} onChange={event => setFooterEmail(event.target.value)} placeholder="Enter your email"/><button type="submit" aria-label="Continue to product enquiry"><ArrowRight /></button></form></div>
        </div>
        <div className="container footer-bottom"><span>© {new Date().getFullYear()} CoopCore. All rights reserved.</span><span className="footer-legal"><Link to="/privacy">Privacy policy</Link><Link to="/terms">Terms of service</Link><Link to="/security">Security</Link></span><span>Made with purpose. <b>Built for cooperatives.</b></span></div>
      </footer>
      <CookieBanner />
    </div>
  );
}
